"use client";

import React from "react";
import { motion } from "framer-motion";
import { MapPin, Building2, Star, Code, Search, FileText, Globe, Compass, Link2, Smartphone, Mic, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const localServices = [{
  id: "gbp-opt",
  icon: MapPin,
  title: "Google Business Profile Optimization",
  description: "Complete setup, category tuning, and geotagged updates to help your business dominate the Google 3-Pack.",
  deliverable: "Category mapping, weekly GBP posts & QA management"
}, {
  id: "citation-building",
  icon: Building2,
  title: "Local Citation Building & Cleanup",
  description: "Standardizing your Name, Address, and Phone (NAP) across authoritative UK and regional business directories.",
  deliverable: "NAP audit, directory submissions & duplicate removal"
}, {
  id: "review-management",
  icon: Star,
  title: "Review Management Strategy",
  description: "Systematic customer feedback workflows that build real trust, boost click-through rates, and protect your brand.",
  deliverable: "Review acquisition workflows & response templates"
}, {
  id: "local-schema",
  icon: Code,
  title: "Local Schema & Structured Data",
  description: "Implementing precise JSON-LD LocalBusiness schema so search engines read your address, hours, and footprint.",
  deliverable: "Geo-coordinates, opening hours & service area markup"
}, {
  id: "local-keywords",
  icon: Search,
  title: "Hyper-Local Keyword Research",
  description: "Uncovering high-intent search queries specific to your target towns, postcodes, and regional neighborhoods.",
  deliverable: "Postcode intent matrix & local competitor gap analysis"
}, {
  id: "onpage-local",
  icon: FileText,
  title: "On-Page Local SEO Optimization",
  description: "Tailoring meta tags, headings, and landing page content with localized intent signals for instant relevance.",
  deliverable: "Localized metadata, headers & embedded service maps"
}, {
  id: "local-content",
  icon: Globe,
  title: "Hyper-Local Content Creation",
  description: "Publishing regional articles and community guides that establish your business as the go-to local authority.",
  deliverable: "Location spotlight articles & targeted community guides"
}, {
  id: "geo-landing-pages",
  icon: Compass,
  title: "Geo-Targeted Landing Pages",
  description: "Building dedicated, non-templated location pages optimized for converting customers in surrounding areas.",
  deliverable: "Tailored regional copy, direct CTAs & driving directions"
}, {
  id: "local-link-building",
  icon: Link2,
  title: "Local Link & PR Outreach",
  description: "Earning contextual backlinks from local press, trade associations, and regional partner organizations.",
  deliverable: "Chamber of Commerce outreach & regional press placement"
}, {
  id: "mobile-local",
  icon: Smartphone,
  title: "Mobile Local UX Optimization",
  description: "Optimizing tap-to-call functions, site speed, and navigation for customers searching on smartphones.",
  deliverable: "Core Web Vitals tuning & instant click-to-call implementation"
}, {
  id: "voice-search",
  icon: Mic,
  title: "Voice & Map Search Optimization",
  description: "Structuring content for conversational voice queries on Apple Siri, Google Assistant, and in-car map systems.",
  deliverable: "FAQ structured data & Apple Maps synchronization"
}, {
  id: "local-audits",
  icon: BarChart3,
  title: "Local SEO Audits & Analysis",
  description: "A comprehensive breakdown of your local map pack positioning, proximity scoring, and rival strategies.",
  deliverable: "Local audit report, proximity matrix & strategic roadmap"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Localseo() {
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium tracking-wide">
            HANDS-ON LOCAL SEO
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Targeted Local SEO Services to Win Your Local Market
          </h2>
          
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed pt-2">
            You work directly with me, Yassine Essaadi. I deliver custom local search strategies designed to drive phone calls, map directions, and qualified leads directly to your business.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {localServices.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} variants={cardVariants} data-index={index}>
                <Card className="h-full border border-border shadow-md hover:shadow-lg transition-[box-shadow,border-color] duration-300 flex flex-col overflow-hidden bg-card text-card-foreground">
                  {}
                  <CardHeader className="bg-primary text-primary-foreground p-6 rounded-t-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="p-2.5 rounded-md bg-primary-foreground/15 text-primary-foreground">
                        <IconComponent className="size-6 shrink-0" />
                      </div>
                      <span className="text-xs font-mono opacity-80 font-medium">
                        0{index + 1}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold leading-snug font-default text-primary-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-6 flex-1 flex flex-col justify-between space-y-6">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </CardDescription>

                    <div className="bg-muted/60 p-3.5 rounded-md border border-border/50 flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <div className="text-xs text-foreground font-medium">
                        <span className="text-muted-foreground font-normal block">Key Output:</span>
                        {service.deliverable}
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="px-6 pb-6 pt-0">
                    <Link to="/ContactPage" className="w-full">
                      <Button variant="outline" className="w-full justify-between group text-xs sm:text-sm">
                        <span>Consult on {service.title.split(" ")[0]}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 text-center bg-muted/40 border border-border rounded-2xl p-8 sm:p-12 max-w-4xl mx-auto shadow-sm">
          <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-3">
            Ready to Dominate Your Local Google Map Pack?
          </h3>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto mb-8">
            Let’s review your current Google Business Profile, local citations, and regional rankings together in a free initial consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/ContactPage">
              <Button size="lg" className="w-full sm:w-auto px-8 font-medium">
                Book a Free Local SEO Audit
              </Button>
            </Link>
            <Link to="/LocalSEOPage">
              <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                Learn More About Local SEO
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}